import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    Collapse,
    Paper,
    Stack,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { RequirementView } from './calculatorApi';

import { CatalogStructure, hasAssignments, idsOf } from './catalogStructure';

/**
 * Der Katalog von oben nach unten, zum Aufklappen.
 *
 * Die ruhige Schwester der Netzansicht: dieselben vier Ebenen, aber ohne Kanten. Wer wissen will,
 * was es in einem Bereich gibt, klappt ihn auf und liest; wer wissen will, was worauf steht, wechselt
 * ins Netz. Zwei Fragen, zwei Darstellungen — und beide erträglich, statt einer, die beides halb kann.
 *
 * **Warum nicht mehr nach Kategorien nebeneinander.** Die erste Fassung ordnete alles in Spalten
 * nach Katalogstruktur. Das ist vollständig und für eine Maschine gut lesbar, beantwortet aber
 * nicht die Frage, die ein Mensch beim Konfigurieren stellt: womit fange ich an, und was hängt
 * davon ab. Also folgt die Anordnung jetzt der Reihenfolge, in der auch kalkuliert wird.
 *
 * **Die vier Ebenen.** Ganz oben die Konfiguration, die in jeder Anlage steckt. Darunter der
 * Unterbau — ohne Server läuft nichts, und je nach Bereich genügt ein anderer. Darunter die
 * Bereiche, jeder mit einer Kante zu dem Unterbau, den er braucht: Funk zu Controller oder
 * Interface, Telefonie auch zur virtuellen Maschine. Ganz unten, aufgeklappt, die einzelnen
 * Anforderungen eines Bereichs.
 *
 * **Warum aufklappbar.** Hundertsechs Anforderungen gleichzeitig sind wieder das Knäuel, das wir
 * loswerden wollten. Die oberen drei Ebenen passen auf einen Blick; was darunter liegt, holt man
 * sich bereichsweise.
 */
export const RequirementLayers: React.FC<{
    requirements: RequirementView[];
    structure: CatalogStructure | null;
    /** Führt in die Fokusansicht — ausgelöst erst vom zweiten Tippen. */
    onOpen: (id: string) => void;
}> = ({ requirements, structure, onOpen }) => {
    const theme = useTheme();

    /** Einmal tippen wählt aus, zweimal springt hinein. Auf dem Tablet gibt es kein Überfahren. */
    const [selected, setSelected] = useState<string | null>(null);
    const [expanded, setExpanded] = useState<string | null>(null);

    const tap = (id: string, open: () => void) => {
        if (selected === id) open();
        else setSelected(id);
    };

    const byId = useMemo(() => new Map(requirements.map((r) => [r.id, r])), [requirements]);

    /**
     * Die Zuordnung kommt vom Server, nicht aus dem Kategorienamen.
     *
     * `RequirementView.category` trägt nur den Blattnamen („analog"), der Pfad im Aufbau den vollen
     * („Funk.analog"). Über Namen verglichen stand bei Funk und Bedienebene eine Null, obwohl beide
     * voll besetzt sind.
     */
    const byPath = useMemo(() => {
        const m = new Map<string, RequirementView[]>();
        for (const g of structure?.groups ?? []) {
            m.set(g.path, idsOf(g).map((id) => byId.get(id)).filter((r): r is RequirementView => !!r));
        }
        return m;
    }, [structure, byId]);

    /** Pflichtanforderungen bilden die oberste Ebene: sie stecken in jeder Anlage. */
    const base = useMemo(
        () => (byPath.get('Konfiguration') ?? []).filter((r) => !r.implicit),
        [byPath],
    );

    /**
     * Die Bereiche der obersten Ebene. Unterkategorien erben, was ihr Oberbereich voraussetzt —
     * „Funk.digital" braucht denselben Unterbau wie „Funk".
     */
    const areas = useMemo(() => {
        if (!structure) return [];
        return structure.groups
            .filter((g) => !g.path.includes('.') && g.path !== 'Konfiguration')
            .map((g) => {
                const subPaths = structure.groups
                    .filter((x) => (x.path === g.path || x.path.startsWith(`${g.path}.`))
                        && idsOf(x).length > 0)
                    .map((x) => x.path)
                    .sort((a, b) => (a === g.path ? -1 : b === g.path ? 1 : a.localeCompare(b)));

                return {
                    ...g,
                    count: subPaths.reduce((sum, p) => sum + (byPath.get(p)?.length ?? 0), 0),
                    subPaths,
                };
            })
            .sort((a, b) => b.count - a.count);
    }, [structure, byPath]);

    const platformTitle = (id: string) => structure?.platforms.find((p) => p.id === id)?.title ?? id;

    if (!structure) {
        return <Alert severity="info">Aufbau wird geladen …</Alert>;
    }

    if (!hasAssignments(structure)) {
        return (
            <Alert severity="warning">
                Der Aufbau kommt ohne Zuordnung der Anforderungen — vermutlich läuft noch eine
                ältere Server-Version. Nach einem Neustart des Kalkulator-Dienstes füllt sich die
                Ansicht.
            </Alert>
        );
    }

    return (
        <Stack spacing={2}>
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                Von oben nach unten: was in jede Anlage gehört, worauf sie steht, und was darauf
                aufsetzt. Einmal tippen wählt aus, noch einmal öffnet.
            </Alert>

            {/* ---------- Ebene 1: was in jeder Anlage steckt ---------- */}
            <Level title="Grundlage — steckt in jeder Anlage">
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {base.map((r) => (
                        <NodeChip
                            key={r.id}
                            id={r.id}
                            title={r.titel || r.id}
                            accent={theme.palette.success.main}
                            selected={selected === r.id}
                            onTap={() => tap(r.id, () => onOpen(r.id))}
                        />
                    ))}
                    {base.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Keine Anforderung in der Kategorie „Konfiguration".
                        </Typography>
                    )}
                </Stack>
            </Level>

            <Connector />

            {/* ---------- Ebene 2: der Unterbau ---------- */}
            <Level title="Unterbau — ohne Server geht nichts">
                <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {structure.platforms.map((p) => (
                        <Paper
                            key={p.id}
                            variant="outlined"
                            sx={{
                                p: 1.25, minWidth: 260, flexGrow: 1, maxWidth: 380,
                                borderTop: `3px solid ${theme.palette.warning.main}`,
                            }}
                        >
                            <Typography variant="subtitle2">{p.title}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                erfüllt durch eine dieser Anforderungen
                            </Typography>
                            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.75 }}>
                                {p.fulfilledBy.map((id) => (
                                    <NodeChip
                                        key={id}
                                        id={id}
                                        title={byId.get(id)?.titel || ''}
                                        accent={theme.palette.warning.main}
                                        selected={selected === id}
                                        onTap={() => tap(id, () => onOpen(id))}
                                        dense
                                    />
                                ))}
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            </Level>

            <Connector />

            {/* ---------- Ebene 3: die Bereiche ---------- */}
            <Level title="Bereiche — was darauf aufsetzt">
                <Stack spacing={1}>
                    {areas.map((a) => {
                        const open = expanded === a.path;
                        return (
                            <Paper key={a.path} variant="outlined" sx={{ overflow: 'hidden' }}>
                                <Box
                                    onClick={() => setExpanded(open ? null : a.path)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1, p: 1.25,
                                        cursor: 'pointer',
                                        borderLeft: `3px solid ${areaAccent(a, theme)}`,
                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                                    }}
                                >
                                    <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} fontSize={12} />
                                    <Typography variant="subtitle2" sx={{ minWidth: 200 }}>
                                        {a.title}
                                    </Typography>
                                    <Chip size="small" variant="outlined" label={`${a.count}`} />

                                    {a.requiredPlatforms.length > 0 ? (
                                        <Tooltip title="Einer dieser Unterbauten genügt — sie sind Alternativen.">
                                            <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap' }}>
                                                {a.requiredPlatforms.map((p, i) => (
                                                    <React.Fragment key={p}>
                                                        {i > 0 && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                oder
                                                            </Typography>
                                                        )}
                                                        <Chip
                                                            size="small"
                                                            color="warning"
                                                            variant="outlined"
                                                            label={platformTitle(p)}
                                                        />
                                                    </React.Fragment>
                                                ))}
                                            </Stack>
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Für diesen Bereich ist noch nicht hinterlegt, worauf er aufsetzt. Das heißt nicht, dass er ohne Unterbau läuft.">
                                            <Chip
                                                size="small"
                                                color="warning"
                                                variant="outlined"
                                                label="Voraussetzung nicht hinterlegt"
                                            />
                                        </Tooltip>
                                    )}

                                    {a.limitedWithout && (
                                        <Tooltip title={`Läuft ohne ${platformTitle(a.limitedWithout)}, dann aber mit Funktionseinschränkungen.`}>
                                            <Box component="span" sx={{ color: 'info.main', display: 'flex' }}>
                                                <FontAwesomeIcon icon={faTriangleExclamation} fontSize={13} />
                                            </Box>
                                        </Tooltip>
                                    )}
                                </Box>

                                <Collapse in={open} unmountOnExit>
                                    <Box sx={{ px: 2, pb: 1.5, pt: 0.5, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                                        {a.subPaths.map((path) => (
                                            <Box key={path} sx={{ mb: 1 }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                                                >
                                                    {path === a.path ? 'direkt' : path.slice(a.path.length + 1)}
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mt: 0.5 }}>
                                                    {(byPath.get(path) ?? []).map((r) => (
                                                        <NodeChip
                                                            key={r.id}
                                                            id={r.id}
                                                            title={r.titel || ''}
                                                            accent={r.implicit ? theme.palette.text.disabled : theme.palette.primary.main}
                                                            selected={selected === r.id}
                                                            onTap={() => tap(r.id, () => onOpen(r.id))}
                                                            dense
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        ))}
                                    </Box>
                                </Collapse>
                            </Paper>
                        );
                    })}
                </Stack>
            </Level>
        </Stack>
    );
};

/** Eine Ebene mit Überschrift. */
const Level: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <Box>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
            {title}
        </Typography>
        <Box sx={{ mt: 0.5 }}>{children}</Box>
    </Box>
);

/** Die senkrechte Linie zwischen zwei Ebenen — sie trägt die Leserichtung. */
const Connector: React.FC = () => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: -1 }}>
            <Box sx={{ width: 2, height: 22, bgcolor: theme.palette.divider }} />
        </Box>
    );
};

/**
 * Ein anklickbarer Knoten.
 *
 * Zwei Stufen statt einer: das erste Tippen wählt aus und zeigt den vollen Titel, das zweite
 * öffnet. Auf einem Tablet gibt es kein Überfahren, und ein Sprung beim ersten Antippen führt
 * regelmäßig dorthin, wo man gar nicht hinwollte.
 */
const NodeChip: React.FC<{
    id: string;
    title: string;
    accent: string;
    selected: boolean;
    onTap: () => void;
    dense?: boolean;
}> = ({ id, title, accent, selected, onTap, dense }) => {
    const theme = useTheme();

    return (
        <Tooltip title={title} enterDelay={500}>
            <Box
                onClick={onTap}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 1, py: dense ? 0.4 : 0.75,
                    maxWidth: dense ? 260 : 340,
                    borderRadius: 0.75, cursor: 'pointer',
                    border: `1px solid ${selected ? accent : theme.palette.divider}`,
                    borderLeft: `3px solid ${accent}`,
                    bgcolor: selected ? alpha(accent, 0.12) : 'background.paper',
                    transition: theme.transitions.create(['background-color', 'border-color']),
                    '&:hover': { bgcolor: alpha(accent, 0.06) },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace', color: accent, flexShrink: 0, fontWeight: 600 }}
                >
                    {id}
                </Typography>
                <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                    {title}
                </Typography>
                {selected && (
                    <Typography variant="caption" sx={{ color: accent, flexShrink: 0, fontWeight: 600 }}>
                        ↵
                    </Typography>
                )}
            </Box>
        </Tooltip>
    );
};

function areaAccent(a: { requiredPlatforms: string[] }, theme: Theme): string {
    return a.requiredPlatforms.length === 0 ? theme.palette.text.disabled : theme.palette.primary.main;
}

export default RequirementLayers;
